import React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Stack from '@mui/material/Stack'
import CaretDownSvg from '../../assets/images/CaretDown.svg'
import { requiredFieldHelperText } from './requiredFieldHelperText'

interface ActivityStudyFieldProps {
    shortTitle: string
    onShortTitleChange: (value: string) => void
    longTitle: string
    onLongTitleChange: (value: string) => void
    objective: string
    onObjectiveChange: (value: string) => void
    disabled?: boolean
    // Keyed by the org's required-fields config (e.g. 'title', 'description')
    requiredFields?: string[]
    fieldErrors?: Record<string, string>
}

const ActivityStudyField: React.FC<ActivityStudyFieldProps> = ({
    shortTitle,
    onShortTitleChange,
    longTitle,
    onLongTitleChange,
    objective,
    onObjectiveChange,
    disabled,
    requiredFields,
    fieldErrors,
}) => {
    const [expanded, setExpanded] = React.useState(true)

    // Auto-expand this section when a failed submit flags one of its fields, so a missing required
    // field can't hide inside a collapsed section. Keyed on a stable boolean so the effect only
    // fires on the false→true transition — it won't re-open a section the user collapses while the
    // error is still unresolved.
    const hasMissingField = ['title', 'titleLong', 'description'].some((f) => !!fieldErrors?.[f])
    React.useEffect(() => {
        if (hasMissingField) setExpanded(true)
    }, [hasMissingField])

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
            <AccordionSummary
                expandIcon={
                    <Box
                        component="img"
                        src={CaretDownSvg}
                        sx={{
                            width: 20,
                            height: 20,
                            transition: 'transform 0.2s',
                            transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        }}
                    />
                }
                sx={{
                    px: 0,
                    py: 1,
                    backgroundColor: 'inherit',
                    '& .MuiAccordionSummary-expandIconWrapper': { transform: 'rotate(0deg)' },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(180deg)' },
                }}
            >
                <Typography variant="18x600">Study</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Short Title
                            {requiredFields?.includes('title') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Short Title"
                            value={shortTitle}
                            disabled={disabled}
                            error={!!fieldErrors?.title}
                            helperText={requiredFieldHelperText('title', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onShortTitleChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Long Title
                            {requiredFields?.includes('titleLong') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Long Title"
                            value={longTitle}
                            disabled={disabled}
                            error={!!fieldErrors?.titleLong}
                            helperText={requiredFieldHelperText('titleLong', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onLongTitleChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Objective
                            {requiredFields?.includes('description') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Objective"
                            value={objective}
                            error={!!fieldErrors?.description}
                            helperText={requiredFieldHelperText('description', requiredFields, fieldErrors)}
                            disabled={disabled}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onObjectiveChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export default ActivityStudyField
